import { isNull, set } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';
import { Options } from 'react-select';

import { useAppSelector } from '../../../../../hooks/react-redux';
import { capitaliseStartLetter } from '../../../../../utils/regex-utils';
import { getAllDropdownLists } from '../../../../admin/dropdown-lists/store/selectors';
import { initialTimeFilter } from '../../../../constants/instanceFilters';
import { Dropdown } from '../../../../shared/dropdown/Dropdown';
import { DropdownOption } from '../../../fields';
import { Comparator, TimeFilterType } from '../../store/filterTypes';
import { ComparatorFilter } from './ComparatorFilter';
import styles from './TableInstanceFilters.module.scss';
import { CustomTooltip } from '../../../../shared/tooltip/Tooltip';
import { Icon } from '../../../../shared/icon/Icon';
import { Delete, Tick } from '../../../../shared/icons';

interface TimeFilterProps {
    updateFilter: (value: TimeFilterType) => void;
    removeFilter: () => void;
    filter: TimeFilterType | null;
    testId: string;
}

export const TimeFilter: React.FC<TimeFilterProps> = ({ updateFilter, removeFilter, filter, testId }) => {
    const timeFilter = useMemo(() => isNull(filter) ? initialTimeFilter : filter, [filter]);
    const { city, comparator, value } = timeFilter;
    const allDropdownLists = useAppSelector(getAllDropdownLists);
    const cityDropdownOptions = useMemo(() => ['Local Time', ...(allDropdownLists.find(({ name }) => name === 'City')?.options || [])].map(option => ({ value: option, label: capitaliseStartLetter(option) })), [allDropdownLists]);

    const cityFilterValue = useMemo(() => {
        if (cityDropdownOptions.length > 0 && !isNull(city)) {
            return cityDropdownOptions.filter(({ value }) => city.includes(value));
        }
        return null;
    }, [cityDropdownOptions, city]);

    const updateTimeFilter = useCallback((key: keyof TimeFilterType, value: string[] | string | Comparator | null) => {
        const otherKeys = Object.keys(timeFilter).filter(otherKey => otherKey !== key);
        if (isNull(value) && otherKeys.every(key => isNull(timeFilter[key as keyof TimeFilterType]))) {
            removeFilter();
            return;
        }
        const updatedFilter = otherKeys.reduce((acc, otherKey) => set(otherKey, timeFilter[otherKey as keyof TimeFilterType], acc), { [`${key}`]: value }) as unknown as TimeFilterType;
        updateFilter(updatedFilter);
    }, [removeFilter, updateFilter, timeFilter]);

    const updateTimeValueFilter = useCallback((value: string) => updateTimeFilter('value', value || null), [updateTimeFilter]);
    const updateComparatorFilter = useCallback((value: Comparator | null) => updateTimeFilter('comparator', value), [updateTimeFilter]);

    const updateCityFilter = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null) => {
        let cityValue = null;
        if (!isNull(dropdownValue) && (dropdownValue as Options<DropdownOption>).length > 0) {
            cityValue = (dropdownValue as Options<DropdownOption>).map(({ value }) => value);
        }
        updateTimeFilter('city', cityValue);
    }, [updateTimeFilter]);

    const initialTimeFilterValue = useMemo(() => typeof value === 'string' && value || '', [value]);
    const [filterTimeValue, setFilterTimeValue] = useState<string>(initialTimeFilterValue);

    const updateTimeFilterKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => e.key.toLowerCase() === 'enter' && updateTimeValueFilter(filterTimeValue), [updateTimeValueFilter, filterTimeValue]);
    const updateTimeFilterClick = useCallback(() => updateTimeValueFilter(filterTimeValue), [updateTimeValueFilter, filterTimeValue]);
    const clearTimeFilter = useCallback(() => {
        setFilterTimeValue('');
        updateTimeValueFilter('');
    }, [updateTimeValueFilter]);

    return (
        <div className={styles.timeFilterWrapper}>
            <div className={styles.cityFilterWrapper}>
                <div className={styles.verticalFilterLabel}>City</div>
                <Dropdown
                    value={cityFilterValue}
                    onChange={updateCityFilter}
                    options={cityDropdownOptions}
                    testId={`${testId}-city-filter`}
                    menuPlacement='bottom'
                    isClearable
                    menuPortalTarget={document.body}
                    isMulti
                />
            </div>
            <div className={styles.verticalFilterLabel}>Time</div>
            <div className={styles.comparatorSplitWrapper}>
                <ComparatorFilter updateFilter={updateComparatorFilter} filter={comparator} testId={testId} />
                <div className={styles.comparedFilterWrapper}>
                    <input
                        data-testid={`${testId}-time-filter`}
                        className={styles.tableFilterInput}
                        onChange={e => setFilterTimeValue(e.target.value)}
                        onKeyDown={updateTimeFilterKeyDown}
                        value={filterTimeValue}
                        type='time'
                    />
                    <CustomTooltip overlayText='Apply Filter'>
                        <button onClick={updateTimeFilterClick} className={styles.setFilterButton}><Icon icon={Tick} fontSize={18} /></button>
                    </CustomTooltip>
                    {filterTimeValue.length > 0 &&
                        <CustomTooltip overlayText='Clear Filter'>
                            <button onClick={clearTimeFilter} className={styles.clearFilterButton}><Icon icon={Delete} fontSize={18} /></button>
                        </CustomTooltip>
                    }
                </div>
            </div>
        </div>
    );
};
